import { spokenPeso } from '../spokenPeso';

export interface ColumnItem {
  key: string;
  label: string;
  valueMinor: number;
}

interface ColumnChartProps {
  items: ColumnItem[];
  ariaLabel: string;
  selectedKey?: string | null;
  onSelect?: (key: string) => void;
  color?: string;
}

const HEIGHT = 96;
const FOCUS =
  'focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-[var(--ring)]';

/** Columns with a text label under each one. The selected column uses the accent. */
export function ColumnChart({
  items,
  ariaLabel,
  selectedKey = null,
  onSelect,
  color = 'var(--chart-1)',
}: ColumnChartProps) {
  const max = Math.max(1, ...items.map((i) => i.valueMinor));
  return (
    <div role="group" aria-label={ariaLabel}>
      <div className="flex items-end gap-1" style={{ height: HEIGHT }}>
        {items.map((item) => {
          const h = Math.max(2, Math.round((Math.max(0, item.valueMinor) / max) * HEIGHT));
          const selected = item.key === selectedKey;
          const name = `${item.label}, ${spokenPeso(item.valueMinor)}`;
          const bar = (
            <svg
              width="100%"
              height={h}
              viewBox={`0 0 10 ${h}`}
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <rect
                x="1"
                y="0"
                width="8"
                height={h}
                rx="2"
                fill={selected ? 'var(--accent)' : color}
              />
            </svg>
          );
          return onSelect ? (
            <button
              key={item.key}
              type="button"
              aria-label={name}
              aria-pressed={selected}
              onClick={() => onSelect(item.key)}
              className={`flex h-full min-w-0 flex-1 items-end rounded ${FOCUS}`}
            >
              {bar}
            </button>
          ) : (
            <div
              key={item.key}
              role="img"
              aria-label={name}
              className="flex h-full min-w-0 flex-1 items-end"
            >
              {bar}
            </div>
          );
        })}
      </div>
      <div className="mt-1 flex gap-1" aria-hidden="true">
        {items.map((item) => (
          <span key={item.key} className="min-w-0 flex-1 truncate text-center text-xs text-muted">
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}
